import {useState} from 'react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useCustomQuery, useCustomMutation} from '@salesforce/commerce-sdk-react'

/**
 * Hook to fetch address suggestions based on address and postal code.
 */
const useAddressSuggestion = (address1) => {
    const [isLoading, setIsLoading] = useState(false)
    const [suggestions, setSuggestions] = useState([])

    const {refetch} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_address1: encodeURIComponent(address1)
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/address-suggestion',
                    apiName: 'address-suggestions'
                }
            },
            rawResponse: false
        },
        {
            enabled: false
        }
    )

    const fetchSuggestions = async ({address, shouldFetch = true}) => {
        if (!shouldFetch || !address) return

        setIsLoading(true)
        try {
            const result = await refetch({
                options: {
                    body: JSON.stringify({
                        address1: address
                    })
                }
            })
            if (!result?.error) {
                setSuggestions(result?.data?.suggestions || [])
            }
        } catch (error) {
            logger.error('Error fetching address suggestions (mock)', {
                namespace: 'useAddressSuggestion',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
    }

    return {
        isLoading,
        suggestions,
        fetchSuggestions
    }
}

const useAddressValidation = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [selectedAddress, setSelectedAddress] = useState({})

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            body: JSON.stringify({
                address1: selectedAddress.address1,
                address2: selectedAddress.apartment,
                city: selectedAddress.city,
                stateCode: selectedAddress.stateCode,
                countryCode: selectedAddress.countryCode,
                postalCode: selectedAddress.postalCode
            }),
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/address-validation',
                apiName: 'address-validation'
            }
        }
    })

    const validateAddress = async ({address, shouldFetch = true}) => {
        if (!shouldFetch) return
        setSelectedAddress(address)
        let data

        setIsLoading(true)
        try {
            data = await mutation.mutateAsync({
                options: {
                    body: JSON.stringify({
                        address1: selectedAddress.address1,
                        address2: selectedAddress.apartment,
                        city: selectedAddress.city,
                        stateCode: selectedAddress.stateCode,
                        countryCode: selectedAddress.countryCode,
                        postalCode: selectedAddress.postalCode
                    })
                }
            })
        } catch (error) {
            logger.error('Error fetching address validation (mock)', {
                namespace: 'useAddressValidation',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        validateAddress
    }
}

export {useAddressSuggestion, useAddressValidation}
